import { Core } from 'chessboard/core/config/types';
import { is } from 'src/utils/app/model/is';
import type { Plugins } from 'chessboard/core/plugins/config/types';
import { reloadOnError } from 'chessboard/plugins/renderer-error-catcher/model/reload-on-error';
import type { Renderer } from 'chessboard/renderers/config/types';
import { RendererErrorCatcher } from 'chessboard/plugins/renderer-error-catcher/config/types';
import { getImportMetaEnvDev } from 'src/utils/import-meta-wrappers';

/**
 * Allows catching and reporting of errors inside the renderer. Ensures that
 * execution doesn't stop so that the user is unaware of the bug.
 */
export function createRendererErrorCatcherPlugin(): Plugins.Model {
  let cachedMethods: Record<string, Function> = {};

  return {
    create,
    destroy,
    match: [
      {
        condition: is(Core.InstanceEvents.RendererSet),
        handler: (_event: Core.Event, context: Core.Context) => create(context),
      },
    ],
    name: RendererErrorCatcher.Plugin.Name,
  };

  /**
   * Wrap the renderer methods (that are responsible for piece movements) so that errors
   * are swallowed and the position is reloaded.
   */
  function create(context: Core.Context) {
    const { options, renderer } = context;

    if (!renderer || getImportMetaEnvDev() || options.test) {
      return;
    }

    ['dragPiece', 'dropPiece', 'makeMove', 'removePiece', 'undoMove'].forEach((key) => {
      const wrappedMethod = reloadOnError({ context, method: renderer[key] });

      cachedMethods[key] = renderer[key];
      renderer[key] = wrappedMethod as Renderer.Model[keyof Renderer.Model];
    });
  }

  /**
   * Reinstate the cached methods.
   */
  function destroy(context: Core.Context) {
    const { renderer } = context;

    if (!renderer) {
      return;
    }

    Object.keys(cachedMethods).forEach((key) => {
      renderer[key] = cachedMethods[key];
    });

    cachedMethods = {};
  }
}
